import { makeWordPressAjaxRequest } from "@/lib/wordpress/ajax";
import { pipe } from "fp-ts/function";
import { fold, mapLeft, toUnion } from "fp-ts/Either";
import { _Error, logErrorsToUiStore } from "@/lib/wordpress/data";
import { globalStore } from "@/store/store-accessor";
import { fpJsonStringify } from "@/util/fp-json-stringify";
import * as t from "io-ts";
import { isUiPanelOpen } from "@/lib/panels/panels";
import { breakdanceUndo } from "@/lib/undo/undo";
import {
  LoadedVariablesDataV,
  Variable,
  VariablesRevisionsList,
  VariablesRevisionsListV
} from "@/oxygen/variables/lib/types";

export function addVariable(newVariable: Variable) {
  breakdanceUndo.transaction(() => {
    // Find the index of the last item with the same collection
    const variables = globalStore.variables;
    let insertIndex = variables.length;

    for (let i = variables.length - 1; i >= 0; i--) {
      if (variables[i].collection === newVariable.collection) {
        insertIndex = i + 1;
        break;
      }
    }

    const newVariables = [
      ...variables.slice(0, insertIndex),
      newVariable,
      ...variables.slice(insertIndex)
    ];

    globalStore.setVariables(newVariables);
  }, "Add Variable");
}

export function addVariableToStoreAndSaveVariablesToWordPressIfVariablesPanelIsClosed(
  variable: Variable
) {
  addVariable(variable);

  if (!isUiPanelOpen("variables")) {
    saveVariablesToWordPress();
  }
}

export async function refetchVariablesFromServerToGlobalStore(): Promise<boolean> {
  const variablesFromServerOrErrors = await makeWordPressAjaxRequest({
    action: "breakdance_load_variables",
    decoder: LoadedVariablesDataV
  });

  return pipe(
    variablesFromServerOrErrors,
    fold(
      errors => {
        logErrorsToUiStore(
          errors.map(err => {
            return {
              ...err,
              customNotificationToShow: err.customNotificationToShow
                ? err.customNotificationToShow.type === "INTERNAL_SERVER_ERROR"
                  ? { ...err.customNotificationToShow, keepOpen: true }
                  : err.customNotificationToShow
                : undefined
            };
          })
        );
        return false;
      },
      loadedData => {
        breakdanceUndo.ignore(() => {
          globalStore.setVariables(loadedData.variables);
          globalStore.setVariablesCollections(loadedData.collections);
          globalStore.markCurrentVariablesAsUntouched();
        });
        return true;
      }
    )
  );
}

export async function saveVariablesToWordPress(): Promise<boolean> {
  return pipe(
    await makeWordPressAjaxRequest({
      friendlyName: "Saving variables to WordPress",
      action: "breakdance_save_variables",
      dataToPost: {
        variables: fpJsonStringify({
          variables: globalStore.variables,
          collections: globalStore.variablesCollections
        })
      },
      decoder: t.unknown
    }),
    fold(
      (errors: _Error[]) => {
        logErrorsToUiStore(errors);
        return false;
      },
      () => {
        breakdanceUndo.ignore(() =>
          globalStore.markCurrentVariablesAsUntouched()
        );
        return true;
      }
    )
  );
}

export async function fetchRemoteVariablesRevisions(): Promise<VariablesRevisionsList | null> {
  return pipe(
    await makeWordPressAjaxRequest({
      action: "breakdance_retrieve_variables_revisions",
      decoder: VariablesRevisionsListV
    }),
    mapLeft(() => null),
    toUnion
  );
}
